import * as DatasetHelper from '../dataset-helper';

/**
 * Builds a result object that calculates selection statistics based on the configuration. The
 * structure of this result object will be:
 *
 * {
 *     "<category value>": {
 *         "countOnSelectedSites": nnn,
 *         "countOnSelectableSites": nnn,
 *         "countOnAllSites": nnn,
 *             "<sub category field name>": {
 *                 "<sub category value>": <count>
 *                 .
 *                 .
 *             },
 *             "subCategoryVariance": <sub category variance value>
 *         }
 *     }
 *     .
 *     .
 *     .
 *     "countOnSelectedSites": nnnn,
 *     "countOnSelectableSites": nnn,
 *     "countOnAllSites": nnn
 * }
 */
export default function calculate (calculationDefn, dataset, referenceSitesHash, selectedSiteIds) {

  const statisticalResult = {};

  // Define the variables that will be used to build totals across all categories for the field.
  let totalCountOnSelectedSites = 0;
  let totalCountOnSelectableSites = 0;
  let totalCountOnAllSites = 0;

  // Only attempt to populate if a dataset is provided.
  if (dataset) {

    // Identify the category field.
    const categoryFieldDefn = calculationDefn.categoryField;
    const categoryField = DatasetHelper.findField(dataset, categoryFieldDefn.fieldId);
    const categoryFieldName = categoryField.fieldName;

    const subCategoryFieldDefn = calculationDefn.subCategoryField;
    const subCategoryField = (subCategoryFieldDefn ? DatasetHelper.findField(dataset, subCategoryFieldDefn.fieldId) : null);
    const subCategoryFieldName = (subCategoryField ? subCategoryField.fieldName : null);

    // Loop through every site.
    Object.keys(referenceSitesHash).forEach((siteId) => {
      const site = referenceSitesHash[siteId];
      const isSiteSelected = selectedSiteIds.includes(siteId);
      const categoryFieldValue = site.data[categoryFieldName];
      const subCategoryFieldValue = (subCategoryFieldDefn ? site.data[subCategoryFieldName] : null);

      // Only process if the category field value exists. Note the '!!' means, 'not not exists',
      // which tests if it exists (positive evaluation).
      if (!!categoryFieldValue) {

        // Ensure the category result object exists.
        if (!statisticalResult[categoryFieldValue]) {
          statisticalResult[categoryFieldValue] = {
            countOnSelectedSites: 0,
            countOnSelectableSites: 0,
            countOnAllSites: 0,
          };
        }
        const categoryResult = statisticalResult[categoryFieldValue];

        // Count.
        if (isSiteSelected) {
          categoryResult.countOnSelectedSites++;
          totalCountOnSelectedSites++;
        }
        if (site.isSelectable) {
          categoryResult.countOnSelectableSites++;
          totalCountOnSelectableSites++;
        }
        categoryResult.countOnAllSites++;
        totalCountOnAllSites++;

        // Perform sub-category increments.
        if (subCategoryFieldDefn && subCategoryFieldName && subCategoryFieldValue) {

          // Ensure the objects/values exist.
          categoryResult[subCategoryFieldName] = categoryResult[subCategoryFieldName] || {};
          categoryResult[subCategoryFieldName][subCategoryFieldValue] =
            categoryResult[subCategoryFieldName][subCategoryFieldValue] || 0.0;

          // Increment if the site is selected.
          if (isSiteSelected) {
            categoryResult[subCategoryFieldName][subCategoryFieldValue]++;
          }
        }

      }

    });

    // If a sub-category was defined, loop through and calculate the mean and variance of the
    // sub-category selections for each category.
    if (subCategoryFieldDefn) {
      categoryField.categories.forEach(category => {
        const categoryResult = statisticalResult[category.value];

        // Calculate mean.
        const subCategoryCount = subCategoryField.categories.length;
        let total = 0.0;
        subCategoryField.categories.forEach(subCategory => {
          total = total + categoryResult[subCategoryFieldName][subCategory.value];
        });
        const mean = total / subCategoryCount;
        categoryResult.subCategoryMean = mean;

        // Calculate variance.
        let varianceAccumulator = 0.0;
        subCategoryField.categories.forEach(subCategory => {
          const value = categoryResult[subCategoryFieldName][subCategory.value] || 0.0;
          varianceAccumulator = varianceAccumulator +
            Math.pow((value - mean), 2);
        });
        const variance = (subCategoryCount > 1 ? varianceAccumulator / (subCategoryCount - 1) : 0.0);
        categoryResult.subCategoryVariance = variance;

      });

    }

  }

  // Assign the totals to the field-level statistics object.
  statisticalResult.countOnSelectedSites = totalCountOnSelectedSites;
  statisticalResult.countOnSelectableSites = totalCountOnSelectableSites;
  statisticalResult.countOnAllSites = totalCountOnAllSites;

  return statisticalResult;

}


// WEBPACK FOOTER //
// ./src/app/utils/calculators/selection-statistics-calculator.js